<template>
  <div class="admin-support-page">
    <div class="page-header">
      <v-btn
        variant="text"
        size="small"
        class="back-btn"
        @click="router.push({ name: 'adminindex' })"
      >
        <v-icon left>mdi-arrow-left</v-icon>
      {{ t('adminSupportIndex.buttons.back') }}
      </v-btn>

      <h1 class="page-title">
      {{ t('adminSupportIndex.pageTitle', { id: ticket?.sup_id || routeId || '' }) }}
      </h1>
      <p class="page-subtitle">
      {{ t('adminSupportIndex.pageSubtitle') }}
      </p>
    </div>

    <div v-if="!isAdmin" class="forbidden-state">
      <v-icon size="64" color="error">mdi-shield-alert</v-icon>
      <h2>{{ t('adminSupportIndex.noAccessTitle') }}</h2>
      <p>{{ t('adminSupportIndex.noAccessDescription') }}</p>
    </div>

    <div v-else>
      <div v-if="isLoading" class="state-block" data-state="loading">
        <v-progress-circular
          indeterminate
          color="#EA5545"
          size="64"
        ></v-progress-circular>
        <p class="state-text">{{ t('adminSupportIndex.loading') }}</p>
      </div>

      <v-alert
        v-else-if="errorMessage"
        type="error"
        variant="tonal"
        class="mb-4"
      >
        {{ errorMessage }}
      </v-alert>

      <v-card
        v-else-if="ticket"
        class="detail-card"
        elevation="1"
      >
        <v-card-text>
              <div class="detail-header">
                <div>
                  <div class="ticket-id-large">#{{ ticket.sup_id }}</div>
                  <h2 class="ticket-title-large">{{ ticket.sup_title }}</h2>
                  <div class="ticket-meta-line">
                    <router-link
                      class="ticket-user-link"
                      :to="{ name: 'adminuserdetail', params: { id: ticket.sup_user_id } }"
                    >
                      <v-icon size="18" color="#666" class="mr-1">mdi-account</v-icon>
                      {{ ticket.user_name }} · {{ ticket.user_email }}
                      <v-icon size="16" color="#EA5545" class="ml-1">mdi-open-in-new</v-icon>
                    </router-link>
                  
                  <div class="ticket-info-lines">
                    <div
                      class="ticket-info-line"
                      v-for="(line, index) in userDetailLines"
                      :key="line.key || index"
                    >
                      <v-icon
                        v-if="line.icon"
                        size="16"
                        color="#666"
                        class="mr-1"
                      >
                        {{ line.icon }}
                      </v-icon>
                      <span class="ticket-info-label">{{ line.label }}:</span>
                      <span>{{ line.value }}</span>
                    </div>
                    <div
                      class="ticket-info-line"
                      v-if="ticket.myp_vertrags_nummer || ticket.myp_id || keydexLink"
                    >
                      <v-icon size="16" color="#666" class="mr-1">mdi-file-document-outline</v-icon>
                      <span class="ticket-info-label">{{ t('adminSupportIndex.labels.contractId', { id: ticket.myp_id }) }}</span>
                      <span v-if="ticket.myp_vertrags_nummer">
                        · {{ t('adminSupportIndex.labels.contractNumber', { number: ticket.myp_vertrags_nummer }) }}
                      </span>
                      <a
                        v-if="keydexLink"
                        :href="keydexLink"
                        target="_blank"
                        rel="noopener"
                        class="keydex-link"
                      >
                        Kundennummer {{ ticket.user_company_kundennummer }}
                      </a>
                    </div>
                  </div>
                    <span
                      v-if="ticket.prod_name"
                      class="ticket-product-large"
                    >
                      <v-icon size="18" color="#EA5545" class="mr-2">mdi-package-variant</v-icon>
                      {{ ticket.prod_name }}
                    </span>
                  </div>
                </div>
                <div class="header-actions">
                  <v-chip
                    :color="getStatusColor(ticket.sup_status)"
                    size="large"
                  >
                    {{ getStatusText(ticket.sup_status) }}
                  </v-chip>

                  <v-btn
                    v-if="ticket.sup_status != 2"
                    color="grey"
                    variant="outlined"
                    @click="updateStatus(2)"
                    size="small"
                    class="ml-2"
                  >
                    <v-icon left size="18">mdi-check</v-icon>
                    {{ t('adminSupportIndex.buttons.markClosed') }}
                  </v-btn>

                  <v-btn
                    v-if="ticket.sup_status == 2"
                    color="#EA5545"
                    variant="outlined"
                    @click="updateStatus(0)"
                    size="small"
                    class="ml-2"
                  >
                    <v-icon left size="18">mdi-refresh</v-icon>
                    {{ t('adminSupportIndex.buttons.reopen') }}
                  </v-btn>

                <v-btn
                  v-if="ticketSupportsBrainSearch"
                  color="#EA5545"
                  variant="flat"
                  size="small"
                  class="ml-2"
                  @click="toggleBrainSearch"
                >
                  <v-icon left size="18">mdi-brain</v-icon>
                  {{
                    showBrainSearch
                      ? t('adminSupportIndex.buttons.hideBrainSearch')
                      : t('adminSupportIndex.buttons.showBrainSearch')
                  }}
                </v-btn>
                </div>
              </div>

              <div class="ticket-info-grid">
                <div class="info-item">
                  <div class="info-label">{{ t('adminSupportIndex.info.created') }}</div>
                  <div class="info-value">{{ formatDate(ticket.sup_created_date) }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">{{ t('adminSupportIndex.info.adminReplies') }}</div>
                  <div class="info-value">
                    <v-icon
                      v-if="ticket.admin_reply_count > 0"
                      size="16"
                      color="#4caf50"
                      class="mr-1"
                    >
                      mdi-reply-all
                    </v-icon>
                    {{ ticket.admin_reply_count }}
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">{{ t('adminSupportIndex.info.newReplies') }}</div>
                  <div class="info-value" :class="{ unread: ticket.unread_count_admin > 0 }">
                    <v-icon
                      v-if="ticket.unread_count_admin > 0"
                      size="16"
                      color="#EA5545"
                      class="mr-1"
                    >
                      mdi-email-alert
                    </v-icon>
                    {{ ticket.unread_count_admin }}
                  </div>
                </div>
                <div class="info-item">
                  <div class="info-label">{{ t('adminSupportIndex.info.brainRequests') }}</div>
                  <div class="info-value brain-info">
                    <v-chip
                      size="small"
                      color="#EA5545"
                      variant="tonal"
                    >
                      {{ brainRequestCount }}
                    </v-chip>
                    <v-btn
                      v-if="ticketSupportsBrainSearch"
                      class="ml-3"
                      size="x-small"
                      variant="text"
                      color="#EA5545"
                      @click="showBrainRequestsDialog = true"
                      :disabled="brainRequestCount === 0"
                    >
                      <v-icon left size="16">mdi-brain</v-icon>
                      {{ t('adminSupportIndex.buttons.showBrainRequests') }}
                    </v-btn>
                  </div>
                </div>
              </div>

              <v-divider class="my-4"></v-divider>

              <div class="ticket-content">
                <h3 class="content-title">{{ t('adminSupportIndex.sections.description') }}</h3>
                <p class="content-text">{{ ticket.sup_text }}</p>
              </div>

              <div
                v-if="ticket.sup_ext_url"
                class="external-link"
              >
                <v-icon left color="#EA5545">mdi-link</v-icon>
                <a
                  :href="ticket.sup_ext_url"
                  target="_blank"
                  class="link-text"
                >
                  {{ ticket.sup_ext_url }}
                </a>
              </div>

              <v-divider class="my-4"></v-divider>

              <div
                v-if="ticket.files && ticket.files.length > 0"
                class="uploaded-files-section"
              >
                <h3 class="content-title">{{ t('adminSupportIndex.sections.attachments') }}</h3>
                <div class="uploaded-files-list">
                  <div
                    v-for="file in ticket.files"
                    :key="file.file_id"
                    class="uploaded-file-item"
                  >
                    <div class="uploaded-file-content" @click="openFile(file)">
                      <v-icon
                        :color="getFileIconColor(file.file_ext)"
                        size="20"
                      >
                        {{ getFileIcon(file.file_ext) }}
                      </v-icon>
                      <span class="uploaded-file-name">
                        {{ file.file_name }}
                        <v-chip
                          v-if="file.file_is_admin === 1 || file.file_is_admin === '1'"
                          size="x-small"
                          color="#EA5545"
                          class="ml-2"
                        >
                          {{ t('adminSupportIndex.labels.adminFile') }}
                        </v-chip>
                      </span>
                      <span class="uploaded-file-date">{{ formatDate(file.file_created_date) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <v-divider class="my-6"></v-divider>

              <!-- Support Conversation (Admin kann hier antworten) -->
              <SupportConversationAdmin
                :supportId="ticket.sup_id"
                :admin-files="ticket.files"
              />
        </v-card-text>
      </v-card>
      <v-dialog
        v-if="ticketSupportsBrainSearch"
        v-model="showBrainSearch"
        max-width="960"
      >
        <v-card>
          <v-card-title class="brain-dialog-title">
            <div>
              <v-icon left color="#EA5545">mdi-brain</v-icon>
              {{ t('adminSupportIndex.sections.brainSearch') }}
            </div>
            <v-spacer></v-spacer>
            <v-btn icon variant="text" @click="showBrainSearch = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <p class="content-text brain-search-hint">
              {{ t('adminSupportIndex.sections.brainSearchHint') }}
            </p>
            <BrainSearch
              :presetProductId="brainSearchProductId"
              :presetQuestion="brainSearchPrompt"
              :lockProductSelection="hasBrainSearchProduct"
              :fullWidth="true"
            />
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="showBrainRequestsDialog"
        max-width="900"
      >
        <v-card class="brain-dialog-card">
          <v-card-title class="brain-dialog-title">
            <div>
              <v-icon left color="#EA5545">mdi-brain</v-icon>
              {{ t('adminSupportIndex.brainRequests.title', { count: brainRequestCount }) }}
            </div>
            <v-spacer></v-spacer>
            <v-btn icon variant="text" @click="showBrainRequestsDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <div v-if="brainRequests.length === 0" class="empty-brain-requests">
              <v-icon size="40" color="#ccc">mdi-emoticon-sad-outline</v-icon>
              <p>{{ t('adminSupportIndex.brainRequests.empty') }}</p>
            </div>
            <div v-else class="brain-request-list">
              <div
                v-for="request in brainRequests"
                :key="request.breq_id"
                class="brain-request-item"
              >
                <div class="brain-request-header">
                  <div class="brain-request-title">
                    <v-icon size="18" color="#EA5545" class="mr-2">mdi-brain</v-icon>
                      {{ t('adminSupportIndex.brainRequests.itemTitle', { id: request.breq_id }) }}
                  </div>
                  <div class="brain-request-date">
                    {{ formatDate(request.breq_created_date) }}
                  </div>
                </div>
                <div class="brain-request-body">
                    <div class="brain-label">{{ t('adminSupportIndex.brainRequests.promptLabel') }}</div>
                  <p class="brain-text">{{ request.breq_prompt || '—' }}</p>
                  <template v-if="request.breq_result">
                      <div class="brain-label mt-2">{{ t('adminSupportIndex.brainRequests.resultLabel') }}</div>
                    <p class="brain-text">{{ request.breq_result }}</p>
                  </template>
                  <div class="brain-meta">
                      <span v-if="request.prod_name">
                        {{ t('adminSupportIndex.brainRequests.meta.product', { value: request.prod_name }) }}
                      </span>
                      <span>
                        {{ t('adminSupportIndex.brainRequests.meta.status', { value: request.breq_status || t('adminSupportIndex.status.unknown') }) }}
                      </span>
                      <span v-if="request.breq_total_tokens">
                        {{ t('adminSupportIndex.brainRequests.meta.tokens', { value: request.breq_total_tokens }) }}
                      </span>
                      <span v-if="request.breq_total_cost">
                        {{ t('adminSupportIndex.brainRequests.meta.cost', { value: Number(request.breq_total_cost).toFixed(4) }) }}
                      </span>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script setup>
import { inject, ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import SupportConversationAdmin from '@/components/objects/SupportConversationAdmin.vue'
import useTranslations from '@/composables/useTranslations'
import BrainSearch from '@/components/objects/BrainSearch.vue'
import { CLEARMENT_TITLE, CLEARMENT_DESCRIPTION } from '@/constants/documentHead'

const baseCalls = inject('baseCalls')
const errorStore = inject('errorStore')
const userStore = inject('userStore')
const updateDocumentHead = inject('updateDocumentHead')
updateDocumentHead?.(CLEARMENT_TITLE, CLEARMENT_DESCRIPTION)

const router = useRouter()
const route = useRoute()
const { t } = useTranslations()

const ticket = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const showBrainRequestsDialog = ref(false)
const showBrainSearch = ref(false)

const routeId = computed(() => {
  const rawId = route.params.id
  if (!rawId && rawId !== 0) return null
  const parsed = Number(rawId)
  return Number.isNaN(parsed) ? null : parsed
})

const userData = computed(() => userStore?.userData || null)

const isAdmin = computed(() => {
  const u = userData.value
  if (!u || !u.user_admin) return false
  return Number(u.user_admin) === 1
})

const brainRequests = computed(() => ticket.value?.brain_requests || [])
const brainRequestCount = computed(() => brainRequests.value.length)
const brainSearchProductId = computed(() => {
  if (!ticket.value) return null
  return (
    ticket.value.sup_product_id ??
    ticket.value.myp_product_id ??
    ticket.value.myp_id ??
    null
  )
})
const keydexLink = computed(() => {
  if (!ticket.value) {
    return null
  }
  const companyId = Number(ticket.value.user_company_id || 0)
  const kundenNr = ticket.value.user_company_kundennummer
  if (companyId !== 1 || !kundenNr) {
    return null
  }
  return `http://www.keydexpro.de/anfrage/kunde/${kundenNr}`
})
const userDetailLines = computed(() => {
  if (!ticket.value) return []
  const lines = []
  if (ticket.value.user_vp_name) {
    lines.push({
      key: 'vp',
      icon: 'mdi-account',
      label: t('adminUserDetail.fields.vpName'),
      value: ticket.value.user_vp_name
    })
  }
  if (ticket.value.user_vn_name) {
    lines.push({
      key: 'vn',
      icon: 'mdi-account-card-details',
      label: t('adminUserDetail.fields.vnName'),
      value: ticket.value.user_vn_name
    })
  }
  if (ticket.value.user_email) {
    lines.push({
      key: 'email',
      icon: 'mdi-email',
      label: 'E-Mail',
      value: ticket.value.user_email
    })
  }
  if (ticket.value.user_strasse || ticket.value.user_ort) {
    lines.push({
      key: 'address',
      icon: 'mdi-home-city',
      label: t('adminUserDetail.fields.street'),
      value: [ticket.value.user_strasse, ticket.value.user_ort].filter(Boolean).join(' · ')
    })
  }
  if (ticket.value.user_company_kundennummer) {
    lines.push({
      key: 'customerNumber',
      icon: 'mdi-identifier',
      label: 'Kundennummer',
      value: ticket.value.user_company_kundennummer
    })
  }
  return lines
})
const ticketSupportsBrainSearch = computed(() => {
  if (!ticket.value) return false
  return Number(ticket.value.prod_brain_search || 0) === 1
})
const hasBrainSearchProduct = computed(
  () =>
    ticketSupportsBrainSearch.value &&
    brainSearchProductId.value !== null &&
    brainSearchProductId.value !== undefined
)
const brainSearchPrompt = computed(() => {
  if (!ticket.value) return ''
  return ticket.value.sup_text || ticket.value.sup_title || ''
})

function formatDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') return t('common.labels.notAvailable')
  const date = new Date(dateString)
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function getStatusColor(status) {
  const colors = {
    0: 'orange',
    1: 'green',
    2: 'grey',
    3: 'red'
  }
  return colors[status] || 'grey'
}

function getStatusText(status) {
  const texts = {
    0: 'adminSupportIndex.status.open',
    1: 'adminSupportIndex.status.done',
    2: 'adminSupportIndex.status.closed',
    3: 'adminSupportIndex.status.urgent'
  }
  const key = texts[status] || 'adminSupportIndex.status.unknown'
  return t(key)
}

function getFileIcon(ext) {
  const icons = {
    pdf: 'mdi-file-pdf-box',
    doc: 'mdi-file-word',
    docx: 'mdi-file-word',
    xls: 'mdi-file-excel',
    xlsx: 'mdi-file-excel',
    ppt: 'mdi-file-powerpoint',
    pptx: 'mdi-file-powerpoint',
    txt: 'mdi-file-document',
    zip: 'mdi-zip-box',
    rar: 'mdi-zip-box',
    jpg: 'mdi-file-image',
    jpeg: 'mdi-file-image',
    png: 'mdi-file-image',
    gif: 'mdi-file-image'
  }
  return icons[ext?.toLowerCase()] || 'mdi-file'
}

function getFileIconColor(ext) {
  const colors = {
    pdf: '#d32f2f',
    doc: '#1976d2',
    docx: '#1976d2',
    xls: '#388e3c',
    xlsx: '#388e3c',
    ppt: '#f57c00',
    pptx: '#f57c00',
    jpg: '#7b1fa2',
    jpeg: '#7b1fa2',
    png: '#7b1fa2',
    gif: '#7b1fa2',
    zip: '#616161',
    rar: '#616161'
  }
  return colors[ext?.toLowerCase()] || '#666'
}

function openFile(file) {
  if (!file || !file.file_hash) return
  window.open(`/getfile/${file.file_hash}`, '_blank')
}

async function loadTicket() {
  if (!routeId.value) {
    errorMessage.value = t('adminSupportIndex.errors.invalidTicketId')
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  try {
    const response = await baseCalls.clearmentPost({
      action: 'getadminsupportdetail',
      sup_id: routeId.value
    })

    if (response.data.success && response.data.support) {
      ticket.value = response.data.support
    } else {
      ticket.value = null
      errorMessage.value =
        response.data.message ||
        t('adminSupportIndex.errors.loadFailed')
      errorStore?.setError?.(true, errorMessage.value)
    }
  } catch (err) {
    console.error('Error loading admin support detail:', err)
    ticket.value = null
    errorMessage.value = t('adminSupportIndex.errors.loadException')
    errorStore?.setError?.(true, errorMessage.value)
  } finally {
    isLoading.value = false
  }
}

async function updateStatus(newStatus) {
  if (!ticket.value) return

  try {
    const response = await baseCalls.clearmentPost({
      action: 'updatesupportstatus',
      sup_id: ticket.value.sup_id,
      sup_status: newStatus
    })

    if (response.data.success) {
      ticket.value.sup_status = newStatus
    } else {
      errorStore?.setError?.(
        true,
        response.data.message || t('adminSupportIndex.errors.statusUpdateFailed')
      )
    }
  } catch (err) {
    console.error('Error updating support status:', err)
    errorStore?.setError?.(
      true,
      t('adminSupportIndex.errors.statusUpdateException')
    )
  }
}

function toggleBrainSearch() {
  showBrainSearch.value = !showBrainSearch.value
}

// Beim ersten Mount nur laden, wenn bereits Admin-Daten vorhanden sind
onMounted(() => {
  if (isAdmin.value) {
    loadTicket()
  }
})

// Wenn sich Admin-Status oder Route-ID ändern (z.B. nach getLoggedUser),
// Ticket nachladen, sobald Admin-Rechte vorhanden sind.
watch(
  () => ({ isAdmin: isAdmin.value, id: routeId.value }),
  (state) => {
    if (state.isAdmin && state.id && !ticket.value && !isLoading.value) {
      loadTicket()
    }
  }
)

watch(ticket, () => {
  showBrainRequestsDialog.value = false
  showBrainSearch.value = false
})
</script>

<style scoped src="@/assets/styles/sfc/components/sites/AdminSupportIndex.css"></style>
